// Formulaires en étapes de la modale « Parler à un commercial » (maquette « 30 sept »).
// Le formulaire Contact Form 7 porte le balisage (anima-website/bin/seed) : chaque
// `fieldset[data-anima-etape]` est une étape, `[data-anima-recap]` le récapitulatif qui contient
// le consentement, l'anti-spam et l'envoi. Ce module ne fait qu'afficher une étape à la fois :
// sans lui, le formulaire reste entier et s'envoie tel quel.
// - Suivant n'est actif que si les champs obligatoires de l'étape sont remplis ;
// - glissement de 300 ms entre étapes (classes is-avant / is-arriere, layout/_modale.scss),
//   barre de progression, « Étape n sur N » dans le surtitre ;
// - après la dernière étape, récapitulatif : une ligne par question, « Modifier » y revient ;
// - CF7 refuse l'envoi (`wpcf7invalid`) : retour à l'étape du premier champ en défaut ;
// - envoi réussi (`wpcf7mailsent`) : « Merci Prénom ».
// Habillages : tuiles photo des familles (`.anima-tuiles`, photos passées par la modale),
// pictos des activités (`.anima-activites`), mois en pastilles (`.anima-mois`), liste
// déroulante changée en curseur (`select.anima-curseur`), champ « précisez » qui n'apparaît que
// si la case « Autre » est cochée (`[data-anima-si-autre]`).

import {
  reducedMotion
} from './dialogue-anima'

const SLIDE = 300 // ms, glissement entre deux étapes
const FOCUS_DELAY = 60 // ms
const EMAIL = /.+@.+\..+/
const ICON_SIZE = 21 // px, pictos des activités
const CHECK_SIZE = 14 // px, coche des cases
const PERCENT = 100

const ICONES = {
  GMS: 'M3 4h2.5l2.2 10.5h10.6L20.5 8H7M9.5 19.5h.01M17 19.5h.01',
  Grossiste: 'M2.5 6.5h11v9h-11zM13.5 9.5h4l3 3v3h-7M6.5 18.8a1.8 1.8 0 1 0 0-3.6 1.8 1.8 0 0 0 0 3.6zM17 18.8a1.8 1.8 0 1 0 0-3.6 1.8 1.8 0 0 0 0 3.6z',
  'Food service': 'M7 13.5V20h10v-6.5M7 13.5a4 4 0 1 1 1.6-7.7 4 4 0 0 1 6.8 0A4 4 0 1 1 17 13.5zM7 17h10',
  Import: 'M12 20.5a8.5 8.5 0 1 0 0-17 8.5 8.5 0 0 0 0 17zM3.5 12h17M12 3.5c2.3 2.3 3.4 5.1 3.4 8.5S14.3 18.2 12 20.5C9.7 18.2 8.6 15.4 8.6 12S9.7 5.8 12 3.5z',
  Autre: 'M6 12h.01M12 12h.01M18 12h.01'
}

const svg = (d, size = ICON_SIZE) => `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${d}"/></svg>`
const COCHE = svg('M5 12.5l4.2 4L19 7', CHECK_SIZE)

const labelText = (input) => {
  const label = input.closest('label')
  const span = label && label.querySelector('.wpcf7-list-item-label')
  return (span || label || input).textContent.trim()
}

// --- Habillages ---------------------------------------------------------------------------
const decorate = (form, familles) => {
  form.querySelectorAll('.anima-tuiles .wpcf7-list-item').forEach((item) => {
    const input = item.querySelector('input')
    const src = familles[labelText(input)]
    const label = item.querySelector('label')
    if (src && label && !label.querySelector('img')) {
      label.insertAdjacentHTML('afterbegin', `<span class="anima-tuile-img"><img src="${src}" alt="" loading="lazy"></span>`)
    }
  })
  form.querySelectorAll('.anima-tuiles .wpcf7-list-item-label, .anima-coches .wpcf7-list-item-label').forEach((span) => {
    span.insertAdjacentHTML('beforeend', `<span class="anima-coche" aria-hidden="true">${COCHE}</span>`)
  })
  form.querySelectorAll('.anima-activites .wpcf7-list-item').forEach((item) => {
    const d = ICONES[labelText(item.querySelector('input'))]
    if (d) {
      item.querySelector('label').insertAdjacentHTML('afterbegin', `<span class="anima-activite-ico">${svg(d)}</span>`)
    }
  })
  form.querySelectorAll('.anima-mois .wpcf7-list-item').forEach((item) => {
    const input = item.querySelector('input')
    const span = item.querySelector('.wpcf7-list-item-label')
    const full = span.textContent.trim()
    input.setAttribute('aria-label', full)
    span.setAttribute('aria-hidden', 'true')
    span.textContent = full.charAt(0)
  })
  form.querySelectorAll('select.anima-curseur').forEach((select) => {
    const n = select.options.length
    const field = select.closest('.anima-form-field') || select.parentElement
    const out = document.createElement('div')
    out.className = 'anima-curseur-box'
    out.innerHTML = `<p class="anima-curseur-val" aria-hidden="true"></p>
      <input type="range" class="anima-range" min="0" max="${n - 1}" step="1">
      <p class="anima-curseur-bornes" aria-hidden="true"><span>${select.options[0].text}</span><span>${select.options[n - 1].text}</span></p>`
    field.appendChild(out)
    select.classList.add('anima-visually-hidden')
    select.tabIndex = -1
    select.setAttribute('aria-hidden', 'true')
    const range = out.querySelector('input')
    const val = out.querySelector('.anima-curseur-val')
    range.setAttribute('aria-label', field.firstChild ? field.firstChild.textContent.trim() : '')
    range.value = Math.max(0, select.selectedIndex)
    const sync = () => {
      select.selectedIndex = Number(range.value)
      const text = select.options[select.selectedIndex].text
      // « 15 palettes par semaine » : le nombre en grand, l'unité à côté (maquette).
      const [, num, unit] = text.match(/^([\d\s\u00a0\u202f]+)\s(.+)$/) || [null, '', text]
      val.innerHTML = num ? '<span class="anima-curseur-num"></span><span></span>' : '<span></span>'
      val.lastElementChild.textContent = unit
      if (num) {
        val.firstElementChild.textContent = num.trim()
      }
      range.setAttribute('aria-valuetext', text)
      range.style.setProperty('--p', `${Math.round(range.value / (n - 1) * PERCENT)}%`)
    }
    range.addEventListener('input', sync)
    sync()
  })
  form.querySelectorAll('[data-anima-si-autre]').forEach((field) => {
    const group = form.querySelector(`[data-name="${field.dataset.animaSiAutre}"]`)
    if (!group) {
      return
    }
    const sync = () => {
      field.hidden = ![...group.querySelectorAll('input:checked')].some((i) => i.value === 'Autre')
    }
    group.addEventListener('change', sync)
    sync()
  })
}

// --- Validation d'une étape (mêmes règles que les champs obligatoires de CF7) ---------------
// Un groupe radio est toujours obligatoire pour CF7, même sans astérisque.
const complete = (step) => [...step.querySelectorAll('.wpcf7-validates-as-required, .wpcf7-radio')].every((el) => {
  if (el.matches('input, textarea, select')) {
    const v = el.value.trim()

    return el.type === 'email' ? EMAIL.test(v) : v !== ''
  }

  return el.querySelector('input:checked') !== null
})

// --- Récapitulatif ------------------------------------------------------------------------
const answers = (step) => {
  const rows = []
  step.querySelectorAll('.wpcf7-form-control-wrap[data-name]').forEach((wrap) => {
    if (wrap.closest('[hidden]')) {
      return
    }
    let value
    const checks = wrap.querySelectorAll('input[type="checkbox"], input[type="radio"]')
    if (checks.length) {
      value = [...checks].filter((i) => i.checked).map((i) => i.value).join(', ')
    } else {
      const el = wrap.querySelector('input, textarea, select')
      value = el && el.tagName === 'SELECT' ? el.options[el.selectedIndex].text : el && el.value.trim()
    }
    if (!value) {
      return
    }
    const field = wrap.closest('.anima-form-field')
    const named = wrap.closest('[data-anima-recap-label]')
    let label = step.querySelector('legend').textContent.trim()
    if (named) {
      label = named.dataset.animaRecapLabel
    } else if (field) {
      label = field.firstChild.textContent.replace('*', '').trim()
    }
    rows.push([label, value])
  })

  return rows
}

// Après l'envoi : le formulaire laisse la place au « Merci Prénom ».
const merci = (form, panel) => {
  const bloc = panel.querySelector('[data-anima-lead-merci]')
  const nom = form.querySelector('[name="nom"]')
  const prenom = nom ? nom.value.trim().split(/\s+/)[0] : ''
  const titre = bloc.querySelector('[data-anima-lead-merci-titre]')
  titre.textContent = prenom ? titre.dataset.modele.replace('%s', prenom) : titre.textContent
  form.hidden = true
  bloc.hidden = false
  bloc.focus({
    preventScroll: true
  })
}

export const initEtapes = (modal, ui) => {
  let familles = {}
  try {
    familles = JSON.parse(modal.dataset.animaFamilles || '{}')
  } catch {
    familles = {}
  }
  const forms = new Map()

  modal.querySelectorAll('[data-anima-lead-panel] .wpcf7-form').forEach((form) => {
    const steps = [...form.querySelectorAll('[data-anima-etape]')]
    const recap = form.querySelector('[data-anima-recap]')
    const panel = form.closest('[data-anima-lead-panel]')
    // Formulaire simple (sans étapes) : seulement le « Merci Prénom » après l'envoi.
    if (!steps.length || !recap) {
      form.addEventListener('wpcf7mailsent', () => merci(form, panel))

      return
    }
    decorate(form, familles)
    form.classList.add('is-etapes')
    const state = {
      form,
      panel,
      steps,
      recap,
      index: 0
    }
    forms.set(panel, state)

    // Entrée dans un champ passe à l'étape suivante au lieu d'envoyer le formulaire.
    form.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.matches('input') && state.index < steps.length) {
        e.preventDefault()
        if (complete(steps[state.index])) {
          ui.go(state, state.index + 1)
        }
      }
    })
    form.addEventListener('input', () => ui.refresh())
    form.addEventListener('change', () => ui.refresh())
    recap.addEventListener('click', (e) => {
      const edit = e.target.closest('[data-anima-recap-edit]')
      if (edit) {
        e.preventDefault()
        ui.go(state, Number(edit.dataset.animaRecapEdit))
      }
    })
    form.addEventListener('wpcf7invalid', () => {
      const bad = form.querySelector('.wpcf7-not-valid')
      const i = bad ? steps.findIndex((s) => s.contains(bad)) : -1
      if (i > -1) {
        ui.go(state, i)
      }
    })
    form.addEventListener('wpcf7mailsent', () => {
      state.done = true
      merci(form, panel)
      ui.refresh()
    })
  })

  return forms
}

// Affiche l'étape `index` (steps.length = récapitulatif) d'un formulaire.
export const showStep = (state, index, dir) => {
  const {
    steps,
    recap
  } = state
  const all = [...steps, recap]
  all.forEach((el, i) => {
    el.classList.toggle('is-active', i === index)
    el.classList.remove('is-avant', 'is-arriere')
  })
  const current = all[index]
  if (dir && !reducedMotion()) {
    current.classList.add(dir > 0 ? 'is-avant' : 'is-arriere')
    setTimeout(() => current.classList.remove('is-avant', 'is-arriere'), SLIDE)
  }
  state.index = index

  if (current === recap) {
    const list = recap.querySelector('[data-anima-recap-liste]')
    list.innerHTML = ''
    steps.forEach((step, i) => {
      answers(step).forEach(([label, value]) => {
        const row = document.createElement('div')
        row.className = 'anima-recap-ligne'
        row.innerHTML = '<dt></dt><dd><span></span><button type="button" class="anima-recap-modifier"></button></dd>'
        row.querySelector('dt').textContent = label
        row.querySelector('dd span').textContent = value
        const btn = row.querySelector('button')
        btn.textContent = recap.dataset.modifier || 'Modifier'
        btn.dataset.animaRecapEdit = i
        list.appendChild(row)
      })
    })
    // Jeton anti-spam neuf au moment d'envoyer (Turnstile expire au bout de quelques minutes).
    if (window.turnstile) {
      recap.querySelectorAll('.cf-turnstile').forEach((w) => {
        try {
          window.turnstile.reset(w)
        } catch {
          // widget pas encore rendu
        }
      })
    }
  }

  const focusable = current.querySelector('input:not([type="hidden"]):not([tabindex="-1"]), textarea, button')
  if (focusable) {
    setTimeout(() => focusable.focus({
      preventScroll: true
    }), FOCUS_DELAY)
  }
}

export const stepComplete = (state) => state.index >= state.steps.length || complete(state.steps[state.index])
